import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  CommissioningDeviceAssignRoomParams,
  CommissioningStore,
  DeviceAssignmentType,
  DevicesStore,
  GatewayStore,
  getTypeOfRemoteDevice,
  InstallationFeaturesStore,
  RemoteDeviceType,
  RoomTypes,
} from '@store';
import {
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsEnabledProperty,
  IsNumberValueProperty,
  RenameHeatingCircuitCommands,
  RenameHeatingCircuitProperties,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';
import { Observable, Subject, of, zip } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { noWhitespaceValidator, uniqueNameValidator } from '../../../../../shared/common';
import { NoSpecialCharactersValidator } from '../../../../../shared/validators/special-characters/special-characters.validator';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

export enum AddRoomOptionType {
  Room,
  NewRoom,
}

export interface RadioOption {
  feature: string;
  type: AddRoomOptionType;
  name: string;
}
@Component({
  templateUrl: './commissioning-add-room-modal.component.html',
  styleUrls: ['./commissioning-add-room-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAddRoomModalComponent implements OnInit, OnDestroy {
  public readonly NEW_ROOM_OPTION_NAME =
    '+SRC_COMMISSIONINGV2.ADD_ROOM_EMBEDED_ADD_NEW_ROOM_OPTION';
  AddRoomOptionType = AddRoomOptionType;

  // Room Radio Group
  radioOptions: RadioOption[];
  selectedOption: RadioOption;

  // Add New Room Forms
  addNewRoomFormGroup: FormGroup<{
    name: FormControl<string>;
    type: FormControl<string>;
  }>;
  roomTypeOptions: string[];
  private cachedAddedRoomName: string;

  // OBNG Vitotrol/eTRV require a channel (heating circuit) to be selected before continuing.
  showChannelSelection = false;
  enabledCircuits: string[] = [];
  selectedChannel: string = null;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public commissioningService: CommissioningService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningAddRoomModalComponent>,
    private commissioningStore: CommissioningStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore,
    private gatewayStore: GatewayStore
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');

    this.roomTypeOptions = Object.keys(RoomTypes);

    this.setupChannelSelection();

    this.installationFeaturesStore.rooms$
      .pipe(
        map((rooms) => {
          return rooms.filter((entry) => {
            return !!entry?.properties?.name?.value;
          });
        }),
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((rooms: Feature<RoomProperties, RoomCommands>[]) => {
        this.constructRoomsOptions(rooms);
        this.constructFormGroup();

        setTimeout(() => {
          this.cdr.markForCheck();
        }, 0);
      });
  }

  addRoom(): void {
    const roomName = this.addNewRoomFormGroup.controls.name.value.trim();
    const roomType = RoomTypes[this.addNewRoomFormGroup.controls.type.value.trim()];

    this.installationFeaturesStore.updateFeature(
      FeatureNames.rooms,
      [roomName, roomType],
      null,
      [FeaturePropertyKey.name, FeaturePropertyKey.type],
      FeatureCommands.add,
      null,
      DeviceIds.RoomControl,
      {
        hideSuccessSnackbar: true,
      }
    );

    this.cachedAddedRoomName = roomName;
  }

  onRoomOptionSelect(roomOption: RadioOption): void {
    this.selectedOption = roomOption;

    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
  }

  onChannelSelect(channel: string): void {
    this.selectedChannel = channel;
  }

  getCircuitName(circuitId: string): Observable<string> {
    const displayedCircuitId = parseInt(circuitId, 10) + 1;
    const defaultName = `${displayedCircuitId}. ${this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.PANEL.CIRCUIT'
    )}`;

    return this.installationFeaturesStore
      .selectInstallationDeviceFeature<RenameHeatingCircuitProperties, RenameHeatingCircuitCommands>(
        FeatureNames.circuitName(circuitId)
      )
      .pipe(
        map((feature) => {
          const circuitName = feature?.properties?.name?.value;

          return circuitName ? `${displayedCircuitId}. ${circuitName}` : defaultName;
        })
      );
  }

  get canNavigateForward(): boolean {
    if (this.selectedOption?.type === AddRoomOptionType.NewRoom) {
      return false;
    }

    return !this.showChannelSelection || this.selectedChannel != null;
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  navigateBack(): void {
    this.commissioningService.modalAction$.next(ModalAction.PreviousStep);
  }

  navigateForward(): void {
    this.commissioningStore.pairedDeviceId$
      .pipe(
        take(1),
        switchMap((pairedDeviceId: string) => {
          return zip(
            this.installationFeaturesStore.selectInstallationDeviceFeature<
              IsNumberValueProperty,
              DeviceHeatingCircuitIdCommands
            >(FeatureNames.deviceHeatingCircuitId, pairedDeviceId),
            this.installationFeaturesStore.selectInstallationDeviceFeature<IsEnabledProperty, {}>(
              FeatureNames.circuits
            ),
            of(pairedDeviceId)
          ).pipe(take(1));
        })
      )
      .subscribe(([deviceHeatingCircuit, enabledHc, pairedDeviceId]) => {
        const firstEnabledHC = enabledHc?.properties?.enabled?.value[0];

        const deviceHc = deviceHeatingCircuit?.properties?.value?.value;
        const hcToAssign = this.showChannelSelection
          ? Number(this.selectedChannel)
          : deviceHc != null
          ? deviceHc
          : Number(firstEnabledHC);

        const assignedRoom = {
          type: DeviceAssignmentType.Room,
          deviceId: pairedDeviceId,
          roomFeature: this.selectedOption.feature,
          roomName: this.selectedOption.name,
        } as CommissioningDeviceAssignRoomParams;

        if (isFinite(hcToAssign) && hcToAssign != null) {
          assignedRoom.heatingCircuitId = hcToAssign;
        }

        this.commissioningStore.deviceAssignRoom(assignedRoom);
        this.commissioningService.modalAction$.next(ModalAction.NextStep);
      });
  }

  private setupChannelSelection(): void {
    this.devicesStore.isSelectedDeviceOBNG$
      .pipe(
        take(1),
        switchMap((isObng) => {
          if (!isObng) {
            return of({ needsChannel: false, circuits: [] as string[] });
          }

          return this.commissioningStore.pairedDeviceId$.pipe(
            take(1),
            switchMap((pairedDeviceId: string) =>
              zip(
                this.gatewayStore.getDeviceRoles(pairedDeviceId),
                this.installationFeaturesStore.selectInstallationDeviceFeature<
                  IsEnabledProperty,
                  {}
                >(FeatureNames.circuits)
              ).pipe(take(1))
            ),
            map(([roles, circuitsFeature]) => {
              const remoteDeviceType = getTypeOfRemoteDevice(roles);
              const needsChannel =
                remoteDeviceType === RemoteDeviceType.TRV ||
                remoteDeviceType === RemoteDeviceType.VITOTROL_100E ||
                remoteDeviceType === RemoteDeviceType.VITOTROL_300E;

              return {
                needsChannel,
                circuits: circuitsFeature?.properties?.enabled?.value ?? [],
              };
            })
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(({ needsChannel, circuits }) => {
        this.showChannelSelection = needsChannel;
        this.enabledCircuits = circuits;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private constructRoomsOptions(rooms: Feature<RoomProperties, RoomCommands>[]): void {
    this.radioOptions = rooms.map((entry) => {
      return {
        feature: entry.feature,
        type: AddRoomOptionType.Room,
        name: entry?.properties?.name?.value,
      };
    });

    this.radioOptions.push({
      feature: null,
      type: AddRoomOptionType.NewRoom,
      name: this.NEW_ROOM_OPTION_NAME,
    });

    const cachedRoom = this.radioOptions.find((entry) => entry.name === this.cachedAddedRoomName);
    this.selectedOption = !!cachedRoom ? cachedRoom : this.radioOptions[0];
  }

  private constructFormGroup(): void {
    this.addNewRoomFormGroup = this.formBuilder.group({
      name: new FormControl('', [
        Validators.required,
        noWhitespaceValidator(),
        Validators.maxLength(40),
        uniqueNameValidator(this.radioOptions.map((entry) => entry.name)),
        NoSpecialCharactersValidator(),
      ]),
      type: new FormControl(this.roomTypeOptions[0]),
    });
  }
}
